import { useState } from 'react';
import EnhancedSEO from '../../Components/SEO/EnhancedSEO';
import { useApplyFoundersCircleMutation } from '../../redux/thunks/unauth/applyfounderscircle';
import {
  Box, Container, Grid, Paper, Typography, Button, TextField, MenuItem,
  Stack, Chip, Alert, useTheme, useMediaQuery,
  Accordion, AccordionSummary, AccordionDetails, CircularProgress
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckIcon,
  Cancel as XIcon,
  Star as StarIcon,
  WorkspacePremium as PremiumIcon,
  Forum as ForumIcon,
} from '@mui/icons-material';

// =============================================================================
// Form option constants (must match Lambda validation)
// =============================================================================

const BUSINESS_TYPES = [
  { value: 'salon',          label: 'Hair salon / barbershop' },
  { value: 'spa',            label: 'Med spa / day spa / beauty services' },
  { value: 'restaurant',     label: 'Restaurant / cafe / food service' },
  { value: 'contractor',     label: 'Contractor / construction / home improvement' },
  { value: 'home_services',  label: 'Home services (HVAC, plumbing, electrical, etc.)' },
  { value: 'auto_services',  label: 'Auto repair / auto services' },
  { value: 'fitness',        label: 'Fitness / personal training / studio' },
  { value: 'retail',         label: 'Local retail / boutique' },
  { value: 'professional',   label: 'Professional services (legal, accounting, etc.)' },
  { value: 'other_service',  label: 'Other local service business' },
];

const TEAM_SIZES = [
  { value: 'solo',     label: 'Just me' },
  { value: '2_3',      label: '2-3 people' },
  { value: '4_10',     label: '4-10 people' },
  { value: '11_15',    label: '11-15 people' },
  { value: 'over_15',  label: 'More than 15 people' },
];

const YEARS_OPERATING = [
  { value: 'under_1', label: 'Less than 1 year' },
  { value: '1_3',     label: '1-3 years' },
  { value: '3_7',     label: '3-7 years' },
  { value: 'over_7',  label: 'More than 7 years' },
];

const LOCATIONS = [
  { value: 'United States', label: 'United States' },
  { value: 'India',         label: 'India' },
];

const WHY_INTERESTED_OPTIONS = [
  { value: 'Save time managing reviews and social posts', label: 'Save time managing reviews and social posts' },
  { value: 'Better tools as my business grows',           label: 'Better tools as my business grows' },
  { value: "Try new AI tools and shape what's built",     label: "Try new AI tools and shape what's built" },
  { value: 'Direct line to founders and influence',       label: 'Direct line to founders and influence' },
  { value: 'Be featured as a Revfer founding customer',   label: 'Be featured as a Revfer founding customer' },
];

const IDEAL_FITS = [
  'Local service business owners (1-15 employees)',
  'Operating for at least a year, with real customers and reviews',
  'Comfortable being publicly featured as a Revfer customer',
  'Willing to share occasional feedback as we evolve the product',
  'In a Revfer-focus vertical (salons, restaurants, contractors, home services)',
];

const NOT_FITS = [
  'Marketing agencies or consultants — we have a separate affiliate program for you',
  'Businesses that prefer to stay anonymous',
  'Multi-location franchises — we focus on independent operators',
  'Anyone looking purely for a free product with no engagement',
];

const FAQ_ITEMS = [
  {
    q: 'What exactly does "free forever" include?',
    a: 'Revfer\'s premium tier — autonomous review responses, social post automation, and all features at the premium level — at no cost for as long as you remain a Founders Circle member in good standing. Future updates to the premium tier are included.'
  },
  {
    q: 'I own multiple businesses. How many get premium access?',
    a: 'Founders Circle membership grants you the same premium plan available to any Revfer customer, which includes up to 5 businesses per user account. If you own more than 5 businesses, you can prioritize which 5 to manage through Revfer, or use separate user accounts for additional businesses (those would be standard paid accounts).'
  },
  {
    q: 'What do you ask of me in return?',
    a: 'Three light commitments: 1) Actually use Revfer for your business — paper-only accounts don\'t count. 2) Allow your name, business name, photo, and quote to be used in our marketing materials. 3) Respond to occasional feedback requests — typically a short survey monthly or a quick 15-minute call quarterly.'
  },
  {
    q: 'Can Revfer remove me from the Founders Circle?',
    a: 'Yes, Revfer reserves the right to revoke Founders Circle membership and special privileges if needed — for example, if the account becomes inactive for an extended period, if terms of service are violated, or if the program structure changes. We commit to communicating clearly and giving reasonable notice if this ever happens. In practice, our intention is for membership to be lifetime — we keep this option to protect the program from misuse.'
  },
  {
    q: 'Will you spam me or make me promote Revfer constantly?',
    a: 'No. Founders Circle is a customer relationship, not a promoter program. We may occasionally feature you in case studies, ads, or investor materials. We won\'t ask you to actively promote Revfer or refer customers — that\'s our separate affiliate program with its own structure.'
  },
  {
    q: 'How is this different from the Affiliate Program?',
    a: 'Founders Circle = customers who use Revfer for their business and let us feature them. Affiliate Program = partners who recommend Revfer to their audience and earn commission. You can\'t be both at the same time — they\'re different relationships with different commercial structures.'
  },
  {
    q: 'How many people will be in the Founders Circle?',
    a: 'We\'re building toward 15 founding customers. Once we reach 15, we may close enrollment or selectively add more on a case-by-case basis.'
  },
  {
    q: 'How long will it take to hear back?',
    a: 'We review every application personally within 5 business days. If approved, we\'ll send a welcome email with a link to schedule a 30-minute kickoff call.'
  },
  {
    q: 'What if I\'m a great fit but already paying for Revfer?',
    a: 'If selected, your existing subscription transitions to free premium going forward. Past payments are not refunded, but you keep using Revfer with no future charges.'
  },
];

const FoundersCirclePage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    business_name: '',
    business_url: '',
    business_type: '',
    team_size: '',
    years_operating: '',
    location: '',
    why_interested: '',
    current_review_management: '',
    public_feature_ok: '',
    website: '', // honeypot
  });

  const [errors, setErrors]           = useState({});
  const [success, setSuccess]         = useState(false);
  const [applicationId, setApplicationId] = useState(null);
  const [applyFoundersCircle, { isLoading }] = useApplyFoundersCircleMutation();

  const validateClient = () => {
    const errs = {};
    if (!formData.full_name.trim())                                  errs.full_name = 'Required';
    if (!formData.email.trim())                                       errs.email = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))      errs.email = 'Enter a valid email';
    if (!formData.business_name.trim())                               errs.business_name = 'Required';
    if (!formData.business_url.trim())                                errs.business_url = 'Required';
    if (!formData.business_type)                                      errs.business_type = 'Required';
    if (!formData.team_size)                                          errs.team_size = 'Required';
    if (!formData.years_operating)                                    errs.years_operating = 'Required';
    if (!formData.location.trim())                                    errs.location = 'Required';
    if (!formData.why_interested.trim())
      errs.why_interested = 'Please pick one';
    if (!formData.current_review_management.trim() ||
        formData.current_review_management.trim().length < 20)
      errs.current_review_management = 'Please give at least 20 characters of detail';
    if (!formData.public_feature_ok)                                  errs.public_feature_ok = 'Required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateClient()) return;
    try {
      const result = await applyFoundersCircle({ payload: formData }).unwrap();
      setApplicationId(result.applicationId);
      setSuccess(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('Application error:', err);

      // Handle server-side validation errors (Lambda returns {error, details})
      if (err?.data?.error === 'validation_failed' && err?.data?.details) {
        setErrors(err.data.details);
        return;
      }

      // Handle duplicate email
      if (err?.data?.error === 'duplicate_email') {
        setErrors({
          email: 'An application with this email already exists.',
        });
        return;
      }

      // Generic error
      setErrors({
        submit: err?.data?.message || 'Something went wrong. Please try again.',
      });
    }
  };

  const setField = (k, v) => {
    setFormData(prev => ({ ...prev, [k]: v }));
    if (errors[k]) setErrors(prev => ({ ...prev, [k]: undefined }));
  };

  return (
    <>
      <EnhancedSEO
        title="Revfer Founders Circle — 15 Founding Customers, Free Premium Forever"
        description="Join Revfer's Founders Circle as one of our 15 founding customers. Free premium forever in exchange for using the product and being featured in our story."
        type="website"
      />

      <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC' }}>

        {/* HERO */}
        <Box sx={{
          background: 'linear-gradient(135deg, #0D2B6B 0%, #1976D2 100%)',
          color: 'white',
          py: { xs: 6, md: 10 },
          px: 2,
        }}>
          <Container maxWidth="md" sx={{ textAlign: 'center' }}>
            <Chip
              label="Founders Circle • Limited to 15"
              size="small"
              sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', mb: 3, fontWeight: 700 }}
            />
            <Typography variant="h2" sx={{
              fontWeight: 800,
              fontSize: { xs: '1.8rem', md: '3rem' },
              mb: 2,
              lineHeight: 1.2,
            }}>
              Help us build Revfer.<br />
              Get premium free, forever.
            </Typography>
            <Typography variant="h6" sx={{
              opacity: 0.9,
              fontWeight: 400,
              maxWidth: 600,
              mx: 'auto',
              mb: 4,
              fontSize: { xs: '1rem', md: '1.1rem' }
            }}>
              We're putting together our founding 15 customers — local service business
              owners who help us shape Revfer in exchange for free premium access for life
              and a permanent place in our story.
            </Typography>
            <Button
              href="#apply"
              variant="contained"
              size="large"
              sx={{
                bgcolor: 'white',
                color: '#0D2B6B',
                fontWeight: 700,
                px: 4,
                py: 1.5,
                '&:hover': { bgcolor: '#F0F7FF' }
              }}
            >
              Apply to the Founders Circle
            </Button>
          </Container>
        </Box>

        {/* THREE BENEFITS */}
        <Container maxWidth="md" sx={{ py: { xs: 5, md: 7 } }}>
          <Grid container spacing={3}>
            {[
              { icon: PremiumIcon, title: 'Premium Forever',  sub: 'Full Revfer premium tier free for life' },
              { icon: ForumIcon,   title: 'Direct Voice',     sub: 'Shape what we build with founder access' },
              { icon: StarIcon,    title: 'Founders Status',  sub: 'Permanent recognition as one of the first 15' },
            ].map(({ icon: Icon, title, sub }) => (
              <Grid key={title} item xs={12} md={4}>
                <Paper elevation={0} sx={{ p: 3, textAlign: 'center', border: '1px solid #E2E8F0', borderRadius: 2, height: '100%' }}>
                  <Icon sx={{ fontSize: 36, color: '#1976D2', mb: 1 }} />
                  <Typography variant="h6" fontWeight={800} color="#0D2B6B" sx={{ mb: 0.5 }}>
                    {title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {sub}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>

        {/* WHO THIS IS FOR / NOT FOR */}
        <Box sx={{ bgcolor: 'white', py: { xs: 5, md: 7 } }}>
          <Container maxWidth="md">
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Typography variant="h5" fontWeight={700} sx={{ mb: 2, color: '#0D2B6B' }}>
                  Who this is for
                </Typography>
                <Stack spacing={1.5}>
                  {IDEAL_FITS.map(item => (
                    <Box key={item} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <CheckIcon sx={{ color: '#2E7D32', fontSize: 20, mt: 0.25 }} />
                      <Typography variant="body1">{item}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h5" fontWeight={700} sx={{ mb: 2, color: '#0D2B6B' }}>
                  Who this isn't for
                </Typography>
                <Stack spacing={1.5}>
                  {NOT_FITS.map(item => (
                    <Box key={item} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <XIcon sx={{ color: '#90A4AE', fontSize: 20, mt: 0.25 }} />
                      <Typography variant="body1" color="text.secondary">{item}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* THE EXCHANGE */}
        <Container maxWidth="md" sx={{ py: { xs: 5, md: 7 } }}>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 4, color: '#0D2B6B', textAlign: 'center' }}>
            The exchange
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 3, border: '1px solid #E2E8F0', borderRadius: 2, height: '100%' }}>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 2, color: '#0D2B6B' }}>
                  What you get
                </Typography>
                <Stack spacing={1.5}>
                  <Typography variant="body2"><strong>Free premium forever</strong> at Revfer's premium tier — autonomous review responses, social post automation, all features included</Typography>
                  <Typography variant="body2"><strong>Up to 5 businesses</strong> under your account get premium access per our standard premium plan</Typography>
                  <Typography variant="body2"><strong>Direct line to the founder</strong> for product questions, feedback, and feature requests</Typography>
                  <Typography variant="body2"><strong>Early access</strong> to new features before public release</Typography>
                  <Typography variant="body2"><strong>Founders Circle status</strong> — permanent recognition as one of our first 15 customers</Typography>
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 3, border: '1px solid #E2E8F0', borderRadius: 2, height: '100%' }}>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 2, color: '#0D2B6B' }}>
                  What you commit to
                </Typography>
                <Stack spacing={1.5}>
                  <Typography variant="body2"><strong>Actually use Revfer</strong> for your business — paper accounts don't count</Typography>
                  <Typography variant="body2"><strong>Allow us to feature you</strong> — your name, business name, photo, and quote in our marketing materials</Typography>
                  <Typography variant="body2"><strong>Share periodic feedback</strong> — monthly short survey or quarterly 15-min call</Typography>
                  <Typography variant="body2"><strong>Be an occasional reference</strong> — when prospective customers want to talk to a real Revfer user</Typography>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Container>

        {/* FAQ */}
        <Box sx={{ bgcolor: 'white', py: { xs: 5, md: 7 } }}>
          <Container maxWidth="md">
            <Typography variant="h5" fontWeight={700} sx={{ mb: 3, color: '#0D2B6B' }}>
              Frequently asked questions
            </Typography>
            {FAQ_ITEMS.map((item, idx) => (
              <Accordion key={idx} elevation={0} sx={{ border: '1px solid #E2E8F0', '&:before': { display: 'none' }, mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight={600}>{item.q}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary">{item.a}</Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Container>
        </Box>

        {/* APPLICATION FORM */}
        <Container maxWidth="sm" sx={{ py: { xs: 5, md: 7 } }} id="apply">
          {success ? (
            <Paper elevation={0} sx={{ p: 5, textAlign: 'center', border: '1px solid #E2E8F0', borderRadius: 2 }}>
              <CheckIcon sx={{ fontSize: 56, color: '#2E7D32', mb: 2 }} />
              <Typography variant="h5" fontWeight={700} sx={{ mb: 1.5, color: '#0D2B6B' }}>
                Application received
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                Thanks, {formData.full_name.split(' ')[0]}. We'll review and reach out
                at {formData.email} within 5 business days.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                If selected, you'll join {formData.business_name} alongside other
                founding customers helping shape Revfer.
              </Typography>
              {applicationId && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3, fontFamily: 'monospace' }}>
                  Reference: {applicationId}
                </Typography>
              )}
            </Paper>
          ) : (
            <Paper elevation={0} sx={{ p: { xs: 3, md: 5 }, border: '1px solid #E2E8F0', borderRadius: 2 }}>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 1, color: '#0D2B6B' }}>
                Apply to the Founders Circle
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Takes about 4 minutes. We review every application personally.
              </Typography>

              <Stack spacing={2.5}>
                <TextField
                  label="Full name"
                  value={formData.full_name}
                  onChange={(e) => setField('full_name', e.target.value)}
                  error={!!errors.full_name}
                  helperText={errors.full_name}
                  fullWidth required
                />

                <TextField
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setField('email', e.target.value)}
                  error={!!errors.email}
                  helperText={errors.email}
                  fullWidth required
                />

                <TextField
                  label="Business name"
                  value={formData.business_name}
                  onChange={(e) => setField('business_name', e.target.value)}
                  error={!!errors.business_name}
                  helperText={errors.business_name}
                  fullWidth required
                />

                <TextField
                  label="Business website or social profile"
                  placeholder="Your website, Google Business listing, or Instagram"
                  value={formData.business_url}
                  onChange={(e) => setField('business_url', e.target.value)}
                  error={!!errors.business_url}
                  helperText={errors.business_url || 'Any link that shows your business'}
                  fullWidth required
                />

                <TextField
                  select
                  label="Type of business"
                  value={formData.business_type}
                  onChange={(e) => setField('business_type', e.target.value)}
                  error={!!errors.business_type}
                  helperText={errors.business_type}
                  fullWidth required
                >
                  {BUSINESS_TYPES.map(t => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  label="Team size"
                  value={formData.team_size}
                  onChange={(e) => setField('team_size', e.target.value)}
                  error={!!errors.team_size}
                  helperText={errors.team_size}
                  fullWidth required
                >
                  {TEAM_SIZES.map(s => (
                    <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  label="How long have you been operating?"
                  value={formData.years_operating}
                  onChange={(e) => setField('years_operating', e.target.value)}
                  error={!!errors.years_operating}
                  helperText={errors.years_operating}
                  fullWidth required
                >
                  {YEARS_OPERATING.map(y => (
                    <MenuItem key={y.value} value={y.value}>{y.label}</MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  label="Where is your business located?"
                  value={formData.location}
                  onChange={(e) => setField('location', e.target.value)}
                  error={!!errors.location}
                  helperText={errors.location}
                  fullWidth required
                >
                  {LOCATIONS.map(l => (
                    <MenuItem key={l.value} value={l.value}>{l.label}</MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  label="What interests you most about the Founders Circle?"
                  value={formData.why_interested}
                  onChange={(e) => setField('why_interested', e.target.value)}
                  error={!!errors.why_interested}
                  helperText={errors.why_interested}
                  fullWidth required
                >
                  {WHY_INTERESTED_OPTIONS.map(o => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </TextField>

                <TextField
                  label="How do you currently handle reviews and social posts?"
                  placeholder="Manual? Software? Ignored? Be honest — there's no wrong answer."
                  value={formData.current_review_management}
                  onChange={(e) => setField('current_review_management', e.target.value)}
                  error={!!errors.current_review_management}
                  helperText={errors.current_review_management || `${formData.current_review_management.length}/500`}
                  multiline rows={3}
                  inputProps={{ maxLength: 500 }}
                  fullWidth required
                />

                <TextField
                  select
                  label="Are you comfortable being publicly featured as a Revfer customer?"
                  value={formData.public_feature_ok}
                  onChange={(e) => setField('public_feature_ok', e.target.value)}
                  error={!!errors.public_feature_ok}
                  helperText={errors.public_feature_ok}
                  fullWidth required
                >
                  <MenuItem value="yes">Yes, fully comfortable</MenuItem>
                  <MenuItem value="yes_limited">Yes, with some limits we can discuss</MenuItem>
                  <MenuItem value="no">No, I prefer to stay private</MenuItem>
                </TextField>

                {/* Honeypot field - hidden from real users */}
                <Box sx={{ position: 'absolute', left: '-9999px', height: 0, width: 0, overflow: 'hidden' }} aria-hidden="true">
                  <input
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={formData.website}
                    onChange={(e) => setField('website', e.target.value)}
                  />
                </Box>

                {errors.submit && (
                  <Alert severity="error">{errors.submit}</Alert>
                )}

                <Button
                  variant="contained"
                  size="large"
                  onClick={handleSubmit}
                  disabled={isLoading}
                  sx={{ py: 1.5, fontWeight: 700 }}
                  startIcon={isLoading && <CircularProgress size={18} color="inherit" />}
                >
                  {isLoading ? 'Submitting...' : 'Apply to Founders Circle'}
                </Button>

                <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', display: 'block' }}>
                  By applying you agree to the Founders Circle terms.
                  We'll review within 5 business days.
                </Typography>
              </Stack>
            </Paper>
          )}
        </Container>
      </Box>
    </>
  );
};

export default FoundersCirclePage;